import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { cartService } from '@/features/cart/services/cartService';
import type {
  CartResponse,
  CartResponseWithRelatedData,
  AddOnResponse
} from '@/features/cart/types';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';

export interface UseCartDataReturn {
  cart: CartResponse | null;
  addOns: AddOnResponse[];
  eventBookingCategories: EventBookingCategoryResponse[];
  cartResponseWithSecret: CartResponseWithRelatedData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCartData(cartId: string | undefined): UseCartDataReturn {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [addOns, setAddOns] = useState<AddOnResponse[]>([]);
  const [eventBookingCategories, setEventBookingCategories] = useState<EventBookingCategoryResponse[]>([]);
  const [cartResponseWithSecret, setCartResponseWithSecret] = useState<CartResponseWithRelatedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track if fetch is in progress to prevent duplicate calls
  const fetchingRef = useRef(false);

  const fetchCart = useCallback(async () => {
    if (!cartId || fetchingRef.current) return;

    // Proactive check: Skip API call if not authenticated
    if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setError('Please log in to view your cart');
      navigate('/login');
      return;
    }

    // Wait for auth check to complete
    if (authLoading) return;

    fetchingRef.current = true;
    try {
      setLoading(true);
      setError(null);
      const response: CartResponseWithRelatedData = await cartService.getCart(cartId);
      setCart(response.cart);

      // TEST MOCK: Override currencies for multi-currency testing
      // Cart currency: GBP, first item: EUR, rest: GBP
      // const testCart = {
      //   ...response.cart,
      //   currency: 'GBP', // Override cart currency to GBP
      //   items: response.cart.items?.map((item, index) => ({
      //     ...item,
      //     currency: index === 0 ? 'EUR' : 'GBP', // First item EUR, rest GBP
      //   })) || null,
      // };

      // setCart(testCart);
      setAddOns(response.add_ons || []);
      setEventBookingCategories(response.event_booking_categories || []);
      setCartResponseWithSecret(response);
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      // If 401, redirect to login (safety net - should be rare if proactive check works)
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number }; status?: number };
        if (axiosError?.response?.status === 401 || axiosError?.status === 401) {
          navigate('/login');
          return;
        }
      }
      setError('Failed to load cart. Please try again later.');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [cartId, navigate, isAuthenticated, authLoading]);

  useEffect(() => {
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, isAuthenticated, authLoading]); // Depend on cartId and auth state

  return {
    cart,
    addOns,
    eventBookingCategories,
    cartResponseWithSecret,
    loading,
    error,
    refetch: fetchCart,
  };
}

